<template>
  <nk-page commercialAccess="Salary" class="workplace-status" policyClaims="Salary_WorkPlaceStatus_Management">
    <template #badges>
      <nk-badge system="salary">
        <nk-badge-item type="system" modelValue="salary"></nk-badge-item>
        <nk-badge-item type="system" modelValue="salary" :title="$t('forms.WorkPlaceStatus')"></nk-badge-item>
      </nk-badge>
    </template>
    <div class="nk-card h-100 d-flex flex-column" startup>
      <div class="h-grow-lock">
        <nk-table v-model="selectedRow" @data-changed="dataChanged" :data="workplaceStatuses" :options="tableOptions" ref="workplaceStatusList" tabindex="101" @row_dbClicked="rowDbClicked">
        </nk-table>
      </div>
    </div>
  </nk-page>
</template>
<script lang="ts">
  import Input from '~/components/general/nikan-input.vue'
  import $sc from '~/plugins/shortcut.collection.js'
  import dialog from '~/plugins/nikan-dialog-plugin'
  import validators, { VCustom } from '~/statics/validators'
  import { defineComponent } from 'vue'
  import { WorkPlaceStatus } from '~/classes/salary/workplace-status'
  import { post } from '~/plugins/nikan-api'
  import Endpoints from '@nikan/endpoints'
  import { IResponseList } from '@/interfaces/base'
  import IsEmpty from 'lodash/isEmpty'
  import { TYPE } from 'vue-toastification'

  export default defineComponent({
    name: 'workplace-status-selector',
    modal: {
      size: 'custom',
      width: 900,
      height: 600,
      stateUID: 'workplace-status-selector',
    },
    props: {
      options: { type: Object },
    },
    data() {
      return {
        workplaceStatuses: [] as WorkPlaceStatus[],
        selectedRow: null as WorkPlaceStatus | null,
        tableOptions: {
          activeRowDisabled: true,
          editable: this.$nikan.checkPermission({ or: ['Salary_WorkPlaceStatus_Add', 'Salary_WorkPlaceStatus_Edit'] }),
          searchable: false,
          allowNewRow: true,
          alwaysEmit: true,
          buttons: {
            addBtn: {
              options: {
                visible: true,
                policyClaims: 'Salary_WorkPlaceStatus_Add',
              }
            },
            deleteBtn: {
              options: {
                visible: true,
                policyClaims: 'Salary_WorkPlaceStatus_Delete',
              }
            }
          },
          displayOrderEnabled: false,
          prepareNewRow: () => new WorkPlaceStatus(),
          columns: [
            {
              name: 'rowNumber',
              title: this.$t('row-number'),
              searchable: true,
              sortable: true,
              editable: false,
              width: 20,
              entering: false,
              pinned: true,
              pinnable: false,
              resizable: false,
            },
            {
              name: 'code',
              title: this.$t('salary.personnel.workplace-status.f.code'),
              edit: {
                component: Input,
                options: {
                  mode: 'strnumber',
                },
              },
              searchable: true,
              sortable: true,
              editable: false,
              width: 40,
              entering: true,
              pinnable: true,
            },
            {
              name: 'finanaceCode',
              title: this.$t('salary.personnel.workplace-status.f.finanaceCode'),
              edit: {
                component: Input,
                options: {
                  mode: 'strnumber',
                },
              },
              searchable: true,
              sortable: true,
              editable: true,
              width: 40,
              entering: true,
              pinnable: true,
              validators: validators('required'),
            },
            {
              name: 'name',
              title: this.$t('salary.personnel.workplace-status.f.name'),
              edit: {
                component: Input,
                options: {
                  mode: 'string',
                },
              },
              searchable: true,
              sortable: false,
              editable: true,
              width: 150,
              validators: validators('required'),
            },
            {
              name: 'taxFreePercent',
              title: this.$t('salary.personnel.workplace-status.f.taxFreePercent'),
              edit: {
                component: Input,
                options: {
                  mode: 'percent',
                },
              },
              searchable: true,
              sortable: false,
              editable: true,
              width: 150,
              validators: validators(
                new VCustom(
                  (cellValue: number | string, row: any) => {
                    return cellValue !== undefined && cellValue !== null && cellValue !== ''
                  },
                  () => this.$t('msg.required', [this.$t('salary.personnel.workplace-status.f.taxFreePercent')])
                )),
            },
          ],
        },
        pageButtons: {
          saveButton: {
            title: this.$t('save'),
            shortcut: $sc.find('save'),
            icon: 'save',
            visible: true,
            disabled: true,
            isPrimary: true,
            handle: () => {
              this.save().then(() => {
                this.$nikan.formUtility.switchMode(this.pageButtons, 'select',)
                this.$refs.workplaceStatusList.table_Focused()
              })
            },
            policyClaims: { or: ['Salary_WorkPlaceStatus_Add', 'Salary_WorkPlaceStatus_Delete'] },
          },
          selectButton: {
            title: this.$t('select'),
            shortcut: $sc.find('save'),
            icon: 'check',
            isPrimary: true,
            visible: true,
            disabled: false,
            handle: () => {

              if (IsEmpty(this.selectedRow))
                return this.$toast(this.$t('document.m.select-row'))

              this.resolve(this.selectedRow)
            }
          }
        }
      }
    },
    mounted() {
      this.prepareWindow(this.$t('salary.personnel.workplace-status.title'), [this.pageButtons.selectButton, this.pageButtons.saveButton])
      this.loadWorkplaceStatus()
    },
    methods: {
      save(): Promise<WorkPlaceStatus[]> {
        return new Promise((resolve, reject) => {
          this.$refs.workplaceStatusList
            .commitChanges()
            .then((workplaces: (WorkPlaceStatus & { rowState: string })[]) => {

              const wait = this.$wait(this.$el)
              post(Endpoints.getUrl('workplaceStatus.modify'), { items: workplaces })
                .then(() => {
                  this.$toast(this.$t('salary.personnel.workplace-status.m.save-success'), { type: TYPE.SUCCESS })
                  this.loadWorkplaceStatus()
                  resolve(workplaces)
                })
                .finally(() => {
                  wait.end()
                })


            })
            .catch((errorRows: { message: string } | { rowNumber: number }[]) => {
              if (Array.isArray(errorRows)) {
                var rowNumbers = errorRows.map((x, i) => x.rowNumber).join(',')
                dialog({
                  title: this.$t('salary.personnel.workplace-status.m.save-workplace-title'),
                  body: this.$t('salary.personnel.workplace-status.m.error-rows', { rowNumbers }),
                  type: 'error',
                  isPrimary: true,
                })
              } else {
                dialog({
                  title: this.$t('salary.personnel.workplace-status.m.save-workplace-title'),
                  body: errorRows.message,
                  type: 'error',
                  isPrimary: true,
                })
              }

              reject(new Error('invalid-rows'))
              this.defer(() => this.$refs.workplaceStatusList.$el.focus(), 100)
            })
        })
      },
      dataChanged() {
        this.$nikan.formUtility.switchMode(this.pageButtons, 'edit')
      },
      rowDbClicked(val: WorkPlaceStatus): void {
        if (this.grandParentType() === 'modal')
          this.resolve(val)
      },
      loadWorkplaceStatus(): void {
        const wait = this.$wait(this.$el)
        post(Endpoints.getUrl('workplaceStatus.listDynamic'))
          .then((res: IResponseList<WorkPlaceStatus>) => {
            this.workplaceStatuses = res.data.items
          })
          .finally(() => {
            wait.end()
          })
      }
    },
  })
</script>
<style></style>
